import {
	Directive,
	ElementRef,
	Input,
	Renderer2,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core';

// --- Facades ---
import { ParametersFacade } from '@facades/parameters.facade';
import { TenantsFacade } from '@facades/tenants.facade';

// --- Interfaces ---
import type { IProduct, IProductVariation } from '@interfaces/product';

interface IElementProps {
	product: IProduct;
	type?: string;
	component?: string;
}

@Directive({
	selector: '[appProductAvailability]',
})
export class ProductAvailabilityDirective {
	constructor(
		private _el: ElementRef,
		private _renderer: Renderer2,
		private _templateRef: TemplateRef<any>,
		private _viewContainer: ViewContainerRef,
		private parametersFacade: ParametersFacade,
		private tenantsFacade: TenantsFacade
	) {}

	get parameters() {
		return this.parametersFacade.parameters;
	}

	get tenant() {
		return this.tenantsFacade.tenant;
	}

	@Input() set appProductAvailability(element: IElementProps) {
		this._viewContainer.clear();

		const isNotCounterType = !element.type || element.type !== 'counter';
		const isInactive = Boolean(element.product?.inactivatedAt);

		if (isInactive) {
			if (isNotCounterType) (this._el.nativeElement as HTMLElement).remove();
			else this.renderUnavailableFlag();
			return;
		}

		const allowSellingParentProduct =
			this.parameters?.allowSellingParentProduct || false;

		const parentStock = this.getTotalStockQuantity(element.product);
		const variationsStock =
			element.product.variations?.reduce(
				(total, variation) => total + this.getTotalStockQuantity(variation),
				0
			) || 0;

		const totalStockQuantity = parentStock + variationsStock;
		const isStockEmpty = totalStockQuantity <= 0;

		const enableVariations = this.tenant?.features?.variations || false;
		const hasVariations = Boolean(element.product?.variations?.length);
		const variationsEnabledAndPresent = enableVariations && hasVariations;

		const variationsHasStock = this.checkStockAvailability(element.product);

		let isAvailable: boolean;
		if (variationsEnabledAndPresent) {
			if (allowSellingParentProduct) {
				isAvailable =
					this.parameters?.allowSellingWithoutStock || totalStockQuantity > 0;
			} else {
				isAvailable =
					this.parameters?.allowSellingWithoutStock || variationsHasStock;
			}
		} else {
			isAvailable =
				this.parameters?.allowSellingWithoutStock || parentStock > 0;
		}

		const showFlag = () => {
			if (isStockEmpty && this.parameters?.showProductsWithoutStockAlert) {
				if (isNotCounterType) this.renderStockFlag(element);
			}
			if (isAvailable) {
				this._viewContainer.createEmbeddedView(this._templateRef);
			}
		};

		if (!variationsEnabledAndPresent) {
			showFlag();
			return;
		}

		if (allowSellingParentProduct) {
			if (element.component) {
				showFlag();
				return;
			}

			const shouldShowStockAlert =
				!variationsHasStock &&
				isStockEmpty &&
				this.parameters?.showProductsWithoutStockAlert;

			if (shouldShowStockAlert && isNotCounterType) {
				this.renderStockFlag(element);
			}

			if (isAvailable) {
				this._viewContainer.createEmbeddedView(this._templateRef);
			}

			return;
		}

		const needsStockWarning =
			!variationsHasStock && this.parameters?.showProductsWithoutStockAlert;

		if (!isAvailable && isNotCounterType && needsStockWarning) {
			this.renderStockFlag(element);
		}

		if (isAvailable) {
			this._viewContainer.createEmbeddedView(this._templateRef);
		}
	}

	// private get defaultStoreId(): number {
	// 	const params = this.parameters;
	// 	if (!params) return 0;

	// 	if (
	// 		params.defaultStorehouseId !== null &&
	// 		params.defaultStorehouseId !== undefined
	// 	) {
	// 		return params.defaultStorehouseId;
	// 	}

	// 	return params.defaultStoreId ?? 0;
	// }

	private checkStockAvailability(product: IProduct) {
		const defaultStoreId =
			this.parameters?.defaultStorehouseId ?? this.parameters?.defaultStoreId;

		return (
			product.variations
				?.map(variation => {
					const defaultStoreStock = variation.stocks?.find(item => {
						const matchesDefaultStoreId = item.storeId === defaultStoreId;
						return matchesDefaultStoreId;
					});
					return defaultStoreStock;
				})
				?.some(stock => {
					const isProductAvailable = (stock?.quantity ?? 0) > 0;
					return isProductAvailable;
				}) ?? false
		);
	}

	private getTotalStockQuantity(product: IProduct | IProductVariation): number {
		const defaultStoreId =
			this.parameters?.defaultStorehouseId ?? this.parameters?.defaultStoreId;

		const totalQuantity =
			product.stocks
				?.filter(stock => {
					const isStockInDefaultStore = stock.storeId === defaultStoreId;
					return isStockInDefaultStore;
				})
				?.reduce((total, stock) => total + stock.quantity, 0) ?? 0;
		return totalQuantity;
	}

	private renderStockFlag(element: IElementProps) {
		const paragraph = this._renderer.createElement('p');
		const stockMessage = !element.type ? 'Sem estoque' : 'S/Estoq.';
		const text = this._renderer.createText(stockMessage);
		const { nativeElement } = this._el;

		this._renderer.appendChild(paragraph, text);
		this._renderer.addClass(paragraph, 'unavailable-product');
		this._renderer.insertBefore(
			nativeElement.parentElement,
			paragraph,
			nativeElement
		);
	}

	private renderUnavailableFlag() {
		const paragraph = this._renderer.createElement('p');
		const text = this._renderer.createText('Indisponível');
		const { nativeElement } = this._el;

		this._renderer.appendChild(paragraph, text);
		this._renderer.addClass(paragraph, 'unavailable-product');
		this._renderer.insertBefore(
			nativeElement.parentElement,
			paragraph,
			nativeElement
		);
	}
}
